/**
 * Finding controls that sit under the system navigation bar (#5810).
 *
 * On Android the app draws edge-to-edge: the page runs all the way down under the system
 * navigation bar (back / home / recents), and the bar's height reaches the page as the CSS
 * variable `--safe-area-inset-bottom`. Content that SCROLLS may pass under the bar — that is what
 * edge-to-edge means, and `cs-content` ends every scroll area with a spacer so the last item can
 * still be scrolled clear of it. A control PINNED to the bottom edge has no such way out: if its
 * offset leaves the inset out, it stays under the bar and cannot be tapped.
 *
 * This file holds the decisions, with no Angular in them, so they can be unit tested. The DOM
 * walk that feeds them lives in `safe-area-overlap-monitor.service.ts`, which reports what it
 * finds to Sentry, and in the E2E test hook of the same name.
 *
 * The static half of the same guard is `scripts/ci/check-bottom-safe-area.mjs`, which refuses a
 * stylesheet that pins something to the bottom without the inset. It cannot see every case — an
 * offset set from code, or one inherited through several rules — which is why this runtime check
 * exists beside it. Rule: `design-docs/scss-and-figma.md`, "Anything pinned to the bottom edge".
 */

import { isGeneratedNameSourceId } from 'directives/cs-capture-cover/host-name-source';

/** The elements a person taps. Anything else under the bar is decoration and not reported. */
export const TAPPABLE_SELECTOR = [
  'button',
  'a[href]',
  'input:not([type="hidden"])',
  'textarea',
  'select',
  '[role="button"]',
  'cs-button',
  'cs-icon-button',
  'cs-fab-button',
].join(', ');

/**
 * How far a control may reach into the bar before it counts. A shadow or a sub-pixel rounding
 * must not raise a report; a control half under the bar, like the one in #5810, reaches ~24px in.
 */
export const OVERLAP_TOLERANCE_PX = 4;

/** One step on the way from a control up to the page, as the DOM walk sees it. */
export interface AncestorStep {
  /** the computed `position` of this element */
  position: string;
  /** the computed `bottom` — for a sticky element, `auto` means it sticks to another edge */
  bottom: string;
  /** whether this element is a vertical scroll area, as `countsAsVerticalScrollArea` decides it */
  scrollsVertically: boolean;
}

/**
 * Whether an element counts as a vertical scroll area for `isPinnedToScreen`.
 *
 * Normally an element set to scroll on the Y axis counts whether or not its content overflows
 * right now: a short cs-content page ends above its bottom spacer, so a control on it cannot
 * reach the bar, and counting only overflowing areas would call every control on a short page
 * pinned. Two kinds of element only count when their content really overflows:
 * - one that also scrolls sideways — CSS turns `overflow-y: visible` into `auto` whenever
 *   `overflow-x` is set, so a horizontal chip row would otherwise read as a vertical scroll area;
 * - one that is itself `fixed` or `sticky` — a panel of our own has no spacer, so a short one
 *   holds its bottom controls still, under the bar if they are there.
 */
export function countsAsVerticalScrollArea(element: {
  position: string;
  overflowX: string;
  overflowY: string;
  contentOverflows: boolean;
}): boolean {
  const scrollableY = element.overflowY === 'auto' || element.overflowY === 'scroll';
  if (!scrollableY) {
    return false;
  }
  const scrollableX = element.overflowX === 'auto' || element.overflowX === 'scroll';
  const pinnedItself = element.position === 'fixed' || element.position === 'sticky';
  // if it also scrolls sideways or is pinned itself, only content that really overflows counts
  if (scrollableX || pinnedItself) {
    return element.contentOverflows;
  }
  return true;
}

/**
 * Whether a control stays where it is on screen while the page scrolls — so that, if it is under
 * the bar, the person cannot scroll it out from under it.
 *
 * `chain` runs from the control itself up to the document root. Walking up:
 * - reaching an element that scrolls, above the control itself, means the control moves with that
 *   element's content, so it is not pinned — even when the scroll area is itself fixed;
 * - a `fixed` element, or a `sticky` one held to the bottom edge, is pinned to the screen (or to
 *   the scroll area's visible edge), so the control is pinned. A sticky element with `bottom: auto`
 *   sticks to another edge and scrolls normally near the bottom, so it decides nothing;
 * - reaching the top without meeting either means nothing scrolls it, so it is pinned.
 */
export function isPinnedToScreen(chain: readonly AncestorStep[]): boolean {
  // for each step up from the control, the first decisive one wins
  for (let index = 0; index < chain.length; index++) {
    const step = chain[index];
    // if an ancestor scrolls, the control scrolls with it, whatever the ancestor's own position
    if (index > 0 && step.scrollsVertically) {
      return false;
    }
    if (step.position === 'fixed' || (step.position === 'sticky' && step.bottom !== 'auto')) {
      return true;
    }
  }
  return true;
}

/**
 * Whether a control's box reaches into the bottom inset by more than the tolerance.
 *
 * A zero inset means there is no bar to be under — the web, a desktop, a phone using gesture
 * navigation with no reported inset — so nothing can overlap it.
 */
export function reachesIntoBottomInset(
  box: { top: number; bottom: number },
  viewportHeight: number,
  insetBottom: number,
): boolean {
  if (insetBottom <= 0) {
    return false;
  }
  const barTop = viewportHeight - insetBottom;
  // a box entirely below the screen is not on screen at all, so it is not "under the bar"
  if (box.top >= viewportHeight) {
    return false;
  }
  // if a box reaches below the screen, it is sliding past the edge: nothing rests there (#6091)
  if (box.bottom > viewportHeight + OVERLAP_TOLERANCE_PX) {
    return false;
  }
  return box.bottom > barTop + OVERLAP_TOLERANCE_PX;
}

/**
 * How far a control's edges may move between two looks and still count as standing still.
 * Covers sub-pixel rounding; a control in the middle of a slide moves tens of pixels.
 */
export const STANDING_STILL_TOLERANCE_PX = 1;

/**
 * Whether a control was in the same place at two looks taken a moment apart (#6091).
 *
 * A box read with `getBoundingClientRect()` includes CSS transforms, so a control caught in the
 * middle of a slide (the tab bar hiding, a page sliding in) can look like it is under the bar
 * for an instant. Only a control that stays put is really stuck there.
 */
export function isStandingStill(
  first: { top: number; bottom: number },
  second: { top: number; bottom: number },
): boolean {
  return Math.abs(first.top - second.top) <= STANDING_STILL_TOLERANCE_PX &&
    Math.abs(first.bottom - second.bottom) <= STANDING_STILL_TOLERANCE_PX;
}

/** An element as `describeControl` names it: its tag and its first classes. */
export interface NamedElement {
  tagName: string;
  classNames: readonly string[];
}

/**
 * A short, stable name for a control, for the report. Built from what a developer would search
 * the code for, in the form `<container> > <control> (no. <index>)`:
 * - the container is the nearest enclosing custom element (a cs-page-action-buttons, a sheet, a
 *   card), with its first two classes, so the report says which part of the page it is in;
 * - the control is its tag, a `data-e2e` or `id`, and its first two classes;
 * - the place says which of the container's elements with that tag it is, counting from 1.
 *   A lone control gets `(no. 1)` too, so its name stays the same if a second one appears.
 * An id a naming helper generated (`cs-name-source-N`) is left out: its number changes every
 * session, so it would only make the name look specific (df-625b549a3b7c). The place is what
 * keeps two otherwise identical buttons in one container apart, which matters because the report
 * is sent once per page and name. Never the control's text, which is translated and may hold the
 * user's own data.
 */
export function describeControl(control: NamedElement & {
  id?: string;
  dataE2e?: string | null;
  /** which of the container's elements with the control's tag it is, counting from 1 */
  place?: number | null;
  container?: NamedElement | null;
}): string {
  let name = control.tagName.toLowerCase();
  if (control.dataE2e) {
    name += `[data-e2e="${control.dataE2e}"]`;
  } else if (control.id && !isGeneratedNameSourceId(control.id)) {
    name += `#${control.id}`;
  }
  name += firstClasses(control);
  if (control.container) {
    name = `${control.container.tagName.toLowerCase()}${firstClasses(control.container)} > ${name}`;
  }
  // only the number, never a total: the total changes whenever the page shows more or fewer such
  // elements, and a new name would mean a second report for the same control
  if (control.place) {
    name += ` (no. ${control.place})`;
  }
  return name;
}

/**
 * The part of an element the two helpers below read. A real DOM element has all of it; the unit
 * tests, which run without a DOM, build plain objects of this shape.
 */
export interface TreeElement {
  tagName: string;
  parentElement: TreeElement | null;
  children: ArrayLike<TreeElement>;
}

/**
 * The nearest element above `control` whose tag has a dash in it — one of our components or a
 * page — or null when there is none below the body. The control itself never counts.
 */
export function nearestCustomElement<T extends TreeElement>(control: T): T | null {
  // for each element above the control, the first custom element is the container
  for (let node = control.parentElement; node && node.tagName !== 'BODY'; node = node.parentElement) {
    if (node.tagName.includes('-')) {
      return node as T;
    }
  }
  return null;
}

/**
 * Which of the elements with `control`'s tag inside `scope` the control is, in document order,
 * counting from 1, or null when it is not inside `scope`. Counting through the whole scope, not
 * just the control's own parent, keeps apart two buttons that each sit alone in a wrapper inside
 * the same container.
 */
export function placeInContainer(control: TreeElement, scope: TreeElement): number | null {
  const sameTag: TreeElement[] = [];
  collectByTag(scope, control.tagName, sameTag);
  const index = sameTag.indexOf(control);
  return index < 0 ? null : index + 1;
}

/** Every descendant of `node` with this tag, in document order, appended to `found`. */
function collectByTag(node: TreeElement, tagName: string, found: TreeElement[]): void {
  // for each child, depth first, keep it if the tag matches and then look inside it
  for (const child of Array.from(node.children)) {
    if (child.tagName === tagName) {
      found.push(child);
    }
    collectByTag(child, tagName, found);
  }
}

/** The first two classes as a selector suffix; more make the name long without making it clearer. */
function firstClasses(element: NamedElement): string {
  return element.classNames.slice(0, 2).map(className => `.${className}`).join('');
}
